'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogAppCTA, BlogWhatTheAppChecks, BlogSources, BlogTLDR, BlogKeyTakeaways, BlogFAQ } from '@/app/components/blog/BlogAeo'

export default function Post17Content() {
  return (
    <BlogPostLayout
      title="10 Worst Ingredients for Microplastics (and How to Spot Them)"
      author="MicroPlastics Team"
      date="April 2, 2025"
      readTime="7 min read"
      heroImage="/blog/app-search.png"
      heroImageAlt="MicroPlastics app ingredient scanner"
    >
      <BlogTLDR>
        <strong>10 ingredient names that mean plastic on cosmetic and personal
        care labels:</strong> Polyethylene, Polypropylene, Polyethylene
        terephthalate (PET), Polyurethane, Polyacrylate, Acrylates copolymer,
        Polyquaternium, Carbomer, PEG (polyethylene glycol), Dimethicone. If
        any of these appear in the first 10 ingredients of a product you use
        daily, it is a swap worth making first. The EU has restricted intentionally
        added microplastics in cosmetics from October 2023; the US has not.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>Watch for &ldquo;poly-&rdquo; prefixes and &ldquo;-mer&rdquo; suffixes, both signal synthetic polymers.</>,
          <>Carbomer is plastic. PEG is plastic. Dimethicone is silicone-polymer-based (a separate category but still concerning).</>,
          <>The EU restricted intentionally added microplastics in cosmetics in October 2023. US still allows them in leave-on products.</>,
          <>Highest-priority categories to audit: toothpaste, lip balm/lipstick, body lotion, shampoo, sunscreen.</>,
          <>Use the MicroPlastics scanner app to parse ingredient lists automatically, no need to memorize the full list.</>,
          <>If you scan for only two words, use <strong>polyethylene</strong> and <strong>polypropylene</strong>, over 90% of cosmetic polymers are polyethylene.</>,
          <>Add <strong>polyethylene-vinyl acetate (EVA)</strong> to your list, it is ~78% of the microplastic content found in toothpaste.</>,
        ]}
      />

      {/* Introduction */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The Hidden Plastics in Your Everyday Products
      </h2>
      <p>
        When most people hear the word &quot;microplastics,&quot; they picture tiny fragments floating in the
        ocean or washing up on beaches. What they do not realize is that millions of consumer products --
        from moisturizers and foundations to sunscreens and shampoos -- contain intentionally added
        microplastic ingredients. These are not contaminants that accidentally ended up in the formula.
        They are deliberately included by manufacturers to serve specific functions like thickening,
        smoothing, film-forming, and exfoliating.
      </p>
      <p>
        The problem is that these synthetic polymer ingredients are rarely labeled in any way that a
        typical consumer would recognize as &quot;plastic.&quot; Instead, they hide behind long chemical
        names that most people skim right over. A 2021 study by the Plastic Soup Foundation found that
        up to 90% of cosmetic products sold in major retail chains contained at least one microplastic
        ingredient. That means nearly every product in your bathroom cabinet could be introducing plastic
        particles into your body through your skin, your lungs, or your digestive system.
      </p>
      <p>
        In this guide, we break down the 10 worst microplastic ingredients found in consumer products
        today. For each one, you will learn what it is, where it is commonly found, what the health
        concerns are, and exactly what to look for on ingredient labels. If you have already read our
        guide on{' '}
        <Link href="/blog/microplastics-in-cosmetics" className="text-[#1E6B47] hover:underline font-medium">
          microplastics in cosmetics
        </Link>, this article goes deeper into the specific chemical culprits behind the contamination.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        2026 Update: Which of These Actually Dominates
      </h2>
      <p>
        This list runs long because ingredient labels use many names. Recent analytical work
        makes clear that the distribution is heavily skewed, so it is worth knowing which
        entries to prioritise.
      </p>
      <p>
        <strong>Over 90% of the polymers used in cosmetics are polyethylene</strong>, with
        polypropylene and polymethyl methacrylate making up most of the remainder. If you
        only ever scan for two words, make them <strong>polyethylene</strong> and{' '}
        <strong>polypropylene</strong> -- that catches the overwhelming majority of
        polymer-containing products.
      </p>
      <p>
        One addition worth making to your scan list:{' '}
        <strong>polyethylene-vinyl acetate (EVA)</strong>. Characterisation of toothpaste
        microplastic content found it composed of roughly{' '}
        <strong>78% polyethylene-vinyl acetate and 22% polyethylene</strong> -- meaning the
        dominant polymer in that category is one most ingredient guides omit entirely.
      </p>
      <p>
        Two regulatory notes that determine how much the label can be trusted in 2026. The US{' '}
        <strong>Microbead-Free Waters Act</strong> covers only <em>rinse-off</em> products, so
        moisturisers, makeup and sunscreen were never in scope. In the EU, rinse-off products
        must comply by <strong>2027</strong> and certain leave-on cosmetics not until{' '}
        <strong>2035</strong>. Reading the ingredient list therefore remains more reliable
        than assuming any product on the shelf has been reformulated.
      </p>

      {/* 1. Polyethylene (PE) */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        1. Polyethylene (PE)
      </h2>
      <h3 className="text-xl font-semibold text-gray-800 mt-6">What It Is</h3>
      <p>
        Polyethylene is the most widely produced plastic in the world, and it is also the most
        common microplastic ingredient found in consumer products. In personal care formulations, it
        appears as tiny beads, powders, or wax-like substances used for exfoliation, texture, and
        moisture retention. It is the same polymer used to make plastic bags, bottles, and packaging
        film, just ground down to microscopic size.
      </p>
      <h3 className="text-xl font-semibold text-gray-800 mt-6">Where It Is Found</h3>
      <p>
        Exfoliating face washes and body scrubs are the most well-known sources, but polyethylene also
        appears in toothpastes, lip balms, mascaras, eyeliners, foundations, hair styling products, and
        even some sunscreens. It is one of the most versatile cosmetic ingredients because manufacturers
        can control its particle size, shape, and texture to fit virtually any product type.
      </p>
      <h3 className="text-xl font-semibold text-gray-800 mt-6">Health Concerns</h3>
      <p>
        Polyethylene microbeads do not dissolve or break down in water. When you rinse them off your
        skin, they pass through wastewater treatment systems and enter rivers, lakes, and oceans, where
        they are ingested by marine life and eventually work their way back into the food chain. On the
        skin itself, microscopic polyethylene particles can penetrate pores and cause irritation. Studies
        have shown that PE particles can absorb environmental toxins like heavy metals and pesticides,
        potentially delivering concentrated pollutants into your body.
      </p>
      <h3 className="text-xl font-semibold text-gray-800 mt-6">What to Look for on Labels</h3>
      <p>
        Look for &quot;Polyethylene,&quot; &quot;PE,&quot; &quot;Polyethylene Glycol&quot; (different but
        related), or &quot;Polyethylene Microspheres.&quot; Some products list it as &quot;Polyethylene
        Powder&quot; or &quot;Polyethylene Beads.&quot;
      </p>

      {/* 2. Polypropylene (PP) */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        2. Polypropylene (PP)
      </h2>
      <h3 className="text-xl font-semibold text-gray-800 mt-6">What It Is</h3>
      <p>
        Polypropylene is the second most commonly produced plastic globally, widely used in food
        packaging, textiles, and automotive parts. In cosmetics and personal care products, it functions
        as a bulking agent, absorbent, and skin-conditioning additive. Like polyethylene, it is a
        synthetic thermoplastic polymer derived from petroleum.
      </p>
      <h3 className="text-xl font-semibold text-gray-800 mt-6">Where It Is Found</h3>
      <p>
        Polypropylene appears frequently in lipsticks, lip glosses, foundations, concealers, blush,
        eyeshadow, and skin care creams. It is particularly common in lip products because it creates
        a smooth, waxy texture that helps formulas glide on evenly. It is also found in some baby care
        products and sunscreens.
      </p>
      <h3 className="text-xl font-semibold text-gray-800 mt-6">Health Concerns</h3>
      <p>
        Polypropylene is considered chemically stable, but that stability is exactly the problem: it
        does not biodegrade. Once in the environment or in your body, PP particles persist indefinitely.
        In lip products, polypropylene is repeatedly ingested in small amounts every time you eat, drink,
        or lick your lips. Over the course of a year, studies estimate that a regular lipstick user may
        ingest several grams of product, including whatever plastic polymers it contains.
      </p>
      <h3 className="text-xl font-semibold text-gray-800 mt-6">What to Look for on Labels</h3>
      <p>
        Search for &quot;Polypropylene,&quot; &quot;PP,&quot; or &quot;Polypropylene Powder.&quot;
      </p>

      {/* 3. Polymethyl Methacrylate (PMMA) */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        3. Polymethyl Methacrylate (PMMA)
      </h2>
      <h3 className="text-xl font-semibold text-gray-800 mt-6">What It Is</h3>
      <p>
        Polymethyl methacrylate is a transparent thermoplastic often used as a lightweight alternative
        to glass, known commercially as Plexiglas or acrylic glass. In cosmetics, PMMA is ground into
        ultra-fine microspheres that scatter light, creating a &quot;soft focus&quot; effect that makes
        skin appear smoother and more even. It is essentially powdered acrylic plastic.
      </p>
      <h3 className="text-xl font-semibold text-gray-800 mt-6">Where It Is Found</h3>
      <p>
        PMMA is common in foundations, primers, anti-aging creams, nail polishes, and pressed powders.
        It is a staple ingredient in products marketed as &quot;blurring,&quot; &quot;smoothing,&quot; or
        &quot;pore-minimizing.&quot; You will also find it in some dermal fillers and dental products,
        though those are medical-grade formulations with different regulations.
      </p>
      <h3 className="text-xl font-semibold text-gray-800 mt-6">Health Concerns</h3>
      <p>
        PMMA microspheres are small enough to penetrate skin pores and accumulate in tissue. Research
        has raised concerns about potential inflammatory responses when PMMA particles lodge in the
        skin or are inhaled as part of pressed powder application. Like all synthetic polymers, PMMA
        does not biodegrade and persists in the body and environment indefinitely.
      </p>
      <h3 className="text-xl font-semibold text-gray-800 mt-6">What to Look for on Labels</h3>
      <p>
        Look for &quot;Polymethyl Methacrylate,&quot; &quot;PMMA,&quot; &quot;Methyl Methacrylate
        Crosspolymer,&quot; or &quot;Acrylate/Methyl Methacrylate Copolymer.&quot;
      </p>

      {/* 4. Nylon-12 (Polyamide) */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        4. Nylon-12 (Polyamide)
      </h2>
      <h3 className="text-xl font-semibold text-gray-800 mt-6">What It Is</h3>
      <p>
        Nylon-12, also known as Polyamide-12, is a synthetic polymer from the nylon family. In
        cosmetics, it is used as a bulking agent, slip modifier, and absorbent. It creates a silky,
        smooth texture in powder-based products and helps formulations adhere to the skin. Nylon is the
        same material used to make stockings, rope, and industrial components, just processed into a
        fine cosmetic-grade powder.
      </p>
      <h3 className="text-xl font-semibold text-gray-800 mt-6">Where It Is Found</h3>
      <p>
        Nylon-12 is prevalent in pressed powders, foundations, mascaras, eyeshadows, blush, bronzers,
        and mattifying products. It is especially common in products designed to control oil and shine,
        as nylon particles absorb excess sebum. It also appears in some deodorants and body powders.
      </p>
      <h3 className="text-xl font-semibold text-gray-800 mt-6">Health Concerns</h3>
      <p>
        As an inhalable microplastic, nylon-12 raises particular concerns in loose powder and spray
        products. When you apply pressed powder with a brush or puff, fine nylon particles can become
        airborne and enter the lungs. Inhalation of synthetic polymer particles has been linked to
        respiratory irritation and potential long-term lung tissue damage. Like other microplastics,
        nylon-12 does not biodegrade.
      </p>
      <h3 className="text-xl font-semibold text-gray-800 mt-6">What to Look for on Labels</h3>
      <p>
        Look for &quot;Nylon-12,&quot; &quot;Polyamide-12,&quot; &quot;Nylon-6,&quot;
        &quot;Nylon-66,&quot; or simply &quot;Nylon&quot; followed by a number.
      </p>

      {/* 5. Polyethylene Terephthalate (PET) */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        5. Polyethylene Terephthalate (PET)
      </h2>
      <h3 className="text-xl font-semibold text-gray-800 mt-6">What It Is</h3>
      <p>
        PET is the same plastic used in soda bottles and food packaging. In cosmetics, it is the
        primary material behind most cosmetic glitter and shimmer effects. PET is processed into thin,
        reflective flakes or films that create sparkle in eyeshadows, body glitter, highlighters, and
        nail polishes. It is one of the most persistent plastics in the environment.
      </p>
      <h3 className="text-xl font-semibold text-gray-800 mt-6">Where It Is Found</h3>
      <p>
        Any product with glitter, shimmer, or metallic effects almost certainly contains PET unless it
        specifically advertises biodegradable or mineral-based alternatives. This includes eyeshadow
        palettes, body shimmer sprays, glitter nail polishes, face glitter, highlighters, and even some
        children&apos;s craft products. PET is also used in some hair products for shine.
      </p>
      <h3 className="text-xl font-semibold text-gray-800 mt-6">Health Concerns</h3>
      <p>
        PET can leach antimony, a toxic metalloid, especially when exposed to heat or UV light. PET
        glitter particles are sharp-edged and can cause micro-abrasions on the skin and eyes. When
        washed off, these particles are too small for most water treatment plants to capture, making
        cosmetic glitter one of the most significant contributors to environmental microplastic
        pollution. Research has detected PET particles in human blood, placental tissue, and lung tissue.
      </p>
      <h3 className="text-xl font-semibold text-gray-800 mt-6">What to Look for on Labels</h3>
      <p>
        Look for &quot;Polyethylene Terephthalate,&quot; &quot;PET,&quot; &quot;PETE,&quot; or the
        generic term &quot;Glitter&quot; (which is PET unless stated otherwise).
      </p>

      {/* 6. Acrylates Copolymer */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        6. Acrylates Copolymer
      </h2>
      <h3 className="text-xl font-semibold text-gray-800 mt-6">What It Is</h3>
      <p>
        Acrylates copolymer is a broad family of synthetic polymers used as film-forming agents,
        binders, and hair fixatives in cosmetic formulations. These polymers create a thin plastic film
        on the skin or hair that provides hold, water resistance, or a smooth finish. Despite their
        liquid or gel-like appearance in products, acrylates copolymers are synthetic plastics in every
        chemical sense.
      </p>
      <h3 className="text-xl font-semibold text-gray-800 mt-6">Where It Is Found</h3>
      <p>
        This is one of the most ubiquitous microplastic ingredients in the personal care industry.
        Acrylates copolymers appear in sunscreens (for water resistance), hair sprays and gels (for
        hold), mascaras (for waterproofing), nail polishes, foundations, primers, and even some
        moisturizers. If a product claims to be &quot;long-lasting,&quot; &quot;waterproof,&quot; or
        &quot;transfer-resistant,&quot; there is a high probability it contains acrylates copolymer.
      </p>
      <h3 className="text-xl font-semibold text-gray-800 mt-6">Health Concerns</h3>
      <p>
        Acrylates copolymers can cause skin sensitization and allergic contact dermatitis in some
        individuals. Because they form a film over the skin, they can trap other chemicals against
        the skin surface, potentially increasing the absorption of other harmful ingredients. When
        washed off, they contribute to microplastic pollution in waterways. The residual monomers
        (unreacted building blocks) in acrylate polymers are known irritants and potential carcinogens.
      </p>
      <h3 className="text-xl font-semibold text-gray-800 mt-6">What to Look for on Labels</h3>
      <p>
        Look for &quot;Acrylates Copolymer,&quot; &quot;Acrylates/C10-30 Alkyl Acrylate
        Crosspolymer,&quot; &quot;Acrylates/Steareth-20 Methacrylate Copolymer,&quot;
        &quot;Ethyl Acrylate,&quot; or any ingredient containing the word
        &quot;acrylate&quot; or &quot;acrylates.&quot;
      </p>

      {/* 7. Polyurethane */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        7. Polyurethane
      </h2>
      <h3 className="text-xl font-semibold text-gray-800 mt-6">What It Is</h3>
      <p>
        Polyurethane is a versatile synthetic polymer used across dozens of industries, from furniture
        foam to automotive coatings. In personal care products, polyurethane serves as a film-forming
        agent and binding ingredient. It creates a flexible, durable coating that helps products adhere
        to skin and hair. It is the same class of plastic used in spray-on coatings, adhesives, and
        synthetic leather.
      </p>
      <h3 className="text-xl font-semibold text-gray-800 mt-6">Where It Is Found</h3>
      <p>
        Polyurethane is commonly found in hair styling products (gels, mousses, and sprays), nail
        polishes and nail hardeners, waterproof mascaras, self-tanning products, and some
        anti-aging serums that promise a &quot;tightening&quot; or &quot;lifting&quot; effect. The
        &quot;hold&quot; you feel from a hair gel or the rigid coating of a nail polish is often
        polyurethane at work.
      </p>
      <h3 className="text-xl font-semibold text-gray-800 mt-6">Health Concerns</h3>
      <p>
        Polyurethane is manufactured using diisocyanates, which are potent respiratory sensitizers and
        known occupational hazards. While the finished polymer is more stable, degradation and heat
        exposure can release isocyanate compounds. Polyurethane coatings on the skin prevent natural
        transpiration and can trap bacteria, potentially causing skin irritation. In nail products,
        prolonged exposure to polyurethane-based formulas has been associated with nail bed damage and
        allergic reactions.
      </p>
      <h3 className="text-xl font-semibold text-gray-800 mt-6">What to Look for on Labels</h3>
      <p>
        Look for &quot;Polyurethane,&quot; &quot;PUR,&quot; &quot;Polyurethane-14,&quot;
        &quot;Polyurethane-34,&quot; or any ingredient with &quot;Polyurethane&quot; followed by a
        number.
      </p>

      {/* 8. Carbomer */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        8. Carbomer
      </h2>
      <h3 className="text-xl font-semibold text-gray-800 mt-6">What It Is</h3>
      <p>
        Carbomer is a family of synthetic polymers made from polyacrylic acid. It functions primarily as
        a thickening agent and emulsion stabilizer, giving gels, creams, and lotions their characteristic
        smooth, thick texture. While many people do not think of carbomer as a &quot;plastic,&quot; it is
        a fully synthetic polymer derived from petroleum-based chemicals. Carbomers are crosslinked
        polyacrylic acids that form a three-dimensional polymer network, and they do not biodegrade in
        the environment.
      </p>
      <h3 className="text-xl font-semibold text-gray-800 mt-6">Where It Is Found</h3>
      <p>
        Carbomer is one of the most widely used ingredients in the entire personal care industry. It
        appears in moisturizers, face gels, hand sanitizers, hair gels, eye creams, serums, toothpaste,
        shaving gels, body lotions, and pharmaceutical gels. Almost any product with a gel-like or
        creamy smooth consistency likely contains a carbomer. It is also extensively used in hand
        sanitizer formulations, meaning global usage spiked dramatically during and after 2020.
      </p>
      <h3 className="text-xl font-semibold text-gray-800 mt-6">Health Concerns</h3>
      <p>
        Carbomers themselves have low acute toxicity, which is why they have been so widely adopted.
        However, the microplastic concern stems from their synthetic polymer nature: carbomers do not
        break down in wastewater or the environment, contributing to the growing burden of polymer
        pollution. Additionally, carbomers can contain residual benzene (a known carcinogen) from the
        manufacturing process. Some individuals also experience skin irritation or allergic reactions
        to carbomer-containing products, particularly at higher concentrations.
      </p>
      <h3 className="text-xl font-semibold text-gray-800 mt-6">What to Look for on Labels</h3>
      <p>
        Look for &quot;Carbomer,&quot; &quot;Carbomer 934,&quot; &quot;Carbomer 940,&quot;
        &quot;Carbomer 941,&quot; &quot;Carbomer Homopolymer,&quot; or &quot;Carbopol.&quot; The
        number following &quot;Carbomer&quot; indicates the specific grade, but all are synthetic
        polyacrylic acid polymers.
      </p>

      {/* 9. Polylactic Acid (PLA) */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        9. Polylactic Acid (PLA)
      </h2>
      <h3 className="text-xl font-semibold text-gray-800 mt-6">What It Is</h3>
      <p>
        Polylactic acid is a synthetic polymer derived from fermented plant starches, typically corn or
        sugarcane. It is heavily marketed as a &quot;biodegradable&quot; or &quot;plant-based&quot;
        plastic and has become the poster child for the &quot;eco-friendly plastics&quot; movement. PLA
        is used in packaging, disposable cutlery, 3D printing, and increasingly in cosmetic and personal
        care products. However, the &quot;biodegradable&quot; label is deeply misleading.
      </p>
      <h3 className="text-xl font-semibold text-gray-800 mt-6">Where It Is Found</h3>
      <p>
        PLA appears in some &quot;eco-friendly&quot; personal care products, biodegradable glitter
        alternatives, green-branded packaging, exfoliating scrubs marketed as sustainable, and
        increasingly in cosmetic sheet masks and single-use applicators. It is also found in tea bags,
        food containers, and disposable cups marketed as compostable.
      </p>
      <h3 className="text-xl font-semibold text-gray-800 mt-6">Health Concerns</h3>
      <p>
        Here is the uncomfortable truth about PLA: it only biodegrades under very specific industrial
        composting conditions, namely sustained temperatures above 140 degrees Fahrenheit (60 degrees
        Celsius) with specific moisture levels and microbial activity. In a landfill, in the ocean, in a
        home compost bin, or in your body, PLA behaves exactly like any other plastic. It does not break
        down. A 2023 study published in Environmental Science and Technology confirmed that PLA fragments
        into microplastics in marine environments just like conventional petroleum-based plastics. So
        while PLA may have a greener origin story, its end-of-life behavior is functionally identical
        to traditional microplastics.
      </p>
      <h3 className="text-xl font-semibold text-gray-800 mt-6">What to Look for on Labels</h3>
      <p>
        Look for &quot;Polylactic Acid,&quot; &quot;PLA,&quot; &quot;Polylactide,&quot; or marketing
        terms like &quot;plant-based plastic,&quot; &quot;corn-based polymer,&quot; or
        &quot;biodegradable plastic.&quot; Be skeptical of any product that claims its plastic
        components are biodegradable without specifying industrial composting conditions.
      </p>

      {/* 10. Dimethicone / Silicones */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        10. Dimethicone / Silicones
      </h2>
      <h3 className="text-xl font-semibold text-gray-800 mt-6">What It Is</h3>
      <p>
        Dimethicone is the most common silicone used in cosmetics and personal care. Silicones are
        synthetic polymers made from silicon, oxygen, carbon, and hydrogen. While technically not
        &quot;plastics&quot; in the traditional carbon-chain sense, silicones are classified as synthetic
        polymers and are increasingly grouped with microplastics by environmental researchers and
        regulators. The European Chemicals Agency (ECHA) includes certain silicones in its microplastic
        restriction proposals. Dimethicone creates a slippery, silky feel and forms a waterproof barrier
        on skin and hair.
      </p>
      <h3 className="text-xl font-semibold text-gray-800 mt-6">Where It Is Found</h3>
      <p>
        Dimethicone and related silicones are among the most pervasive cosmetic ingredients on the
        market. They appear in shampoos, conditioners, leave-in hair treatments, foundations, primers,
        moisturizers, sunscreens, BB creams, CC creams, anti-frizz serums, body lotions, deodorants,
        and shaving creams. Virtually any product that claims to be &quot;smoothing,&quot;
        &quot;silky,&quot; or &quot;hydrating&quot; likely relies on silicone polymers.
      </p>
      <h3 className="text-xl font-semibold text-gray-800 mt-6">Health Concerns</h3>
      <p>
        Dimethicone forms an occlusive barrier on the skin that can trap dirt, bacteria, sebum, and
        other chemicals underneath, potentially leading to clogged pores, breakouts, and increased
        absorption of other harmful ingredients. On hair, silicone buildup can cause dryness, breakage,
        and scalp irritation over time. Environmentally, silicones are persistent pollutants. They do
        not biodegrade in water and accumulate in aquatic sediments, where they have been shown to
        affect marine organisms. Cyclic silicones like cyclomethicone (D4 and D5) are classified as
        endocrine disruptors by the EU and have been restricted in wash-off cosmetics in Europe.
      </p>
      <h3 className="text-xl font-semibold text-gray-800 mt-6">What to Look for on Labels</h3>
      <p>
        Look for &quot;Dimethicone,&quot; &quot;Cyclomethicone,&quot; &quot;Cyclopentasiloxane,&quot;
        &quot;Dimethiconol,&quot; &quot;Phenyl Trimethicone,&quot; &quot;Amodimethicone,&quot; or any
        ingredient ending in &quot;-cone,&quot; &quot;-conol,&quot; or &quot;-siloxane.&quot; The
        &quot;-cone&quot; suffix is the quickest way to identify silicone ingredients.
      </p>

      {/* How to Read Labels */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        How to Read Labels Like a Microplastics Expert
      </h2>
      <p>
        Now that you know the 10 worst offenders, here is a practical system for reading any product
        label and identifying microplastic ingredients quickly:
      </p>
      <ol className="list-decimal pl-6 space-y-3">
        <li>
          <strong>Look for the &quot;poly-&quot; prefix.</strong> Any ingredient that starts with
          &quot;poly&quot; is a polymer. While not every polymer is a microplastic, this prefix is your
          fastest filter. Polyethylene, polypropylene, polymethyl methacrylate, polyurethane, polylactic
          acid -- they all start with &quot;poly.&quot;
        </li>
        <li>
          <strong>Watch for the &quot;-copolymer&quot; suffix.</strong> Ingredients ending in
          &quot;copolymer&quot; are synthetic polymers made from two or more monomer types. Acrylates
          copolymer, VP/VA copolymer, and styrene/acrylates copolymer are all microplastic ingredients.
        </li>
        <li>
          <strong>Check for &quot;-cone&quot; and &quot;-siloxane&quot; endings.</strong> These indicate
          silicone-based synthetic polymers. Dimethicone, cyclomethicone, and cyclopentasiloxane are the
          most common.
        </li>
        <li>
          <strong>Beware of &quot;nylon&quot; in any form.</strong> Nylon-6, Nylon-12, Nylon-66, and
          Polyamide are all synthetic microplastic ingredients regardless of the number attached.
        </li>
        <li>
          <strong>Understand ingredient order.</strong> Ingredients are listed in descending order of
          concentration. If a microplastic ingredient appears in the first half of the list, the product
          contains a significant amount. If it appears in the last third, the concentration is lower but
          still present.
        </li>
        <li>
          <strong>Be skeptical of marketing claims.</strong> Terms like &quot;natural,&quot;
          &quot;clean,&quot; &quot;green,&quot; and &quot;eco-friendly&quot; are unregulated. A product
          can use these terms on its packaging while containing multiple synthetic polymer ingredients.
          Always read the actual ingredient list, not the front label.
        </li>
      </ol>

      {/* Quick Reference Table */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Quick Reference: Red Flag Keywords
      </h2>
      <div className="overflow-x-auto my-6">
        <table className="w-full border-collapse border border-gray-200 text-sm md:text-base">
          <thead>
            <tr className="bg-[#1E6B47]/10">
              <th className="border border-gray-200 px-4 py-3 text-left font-semibold text-gray-800">Pattern</th>
              <th className="border border-gray-200 px-4 py-3 text-left font-semibold text-gray-800">Examples</th>
              <th className="border border-gray-200 px-4 py-3 text-left font-semibold text-gray-800">Type</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border border-gray-200 px-4 py-3 font-medium">Starts with &quot;Poly-&quot;</td>
              <td className="border border-gray-200 px-4 py-3">Polyethylene, Polypropylene, Polyurethane</td>
              <td className="border border-gray-200 px-4 py-3">Petroleum-based plastics</td>
            </tr>
            <tr className="bg-gray-50">
              <td className="border border-gray-200 px-4 py-3 font-medium">Contains &quot;Copolymer&quot;</td>
              <td className="border border-gray-200 px-4 py-3">Acrylates Copolymer, VP/VA Copolymer</td>
              <td className="border border-gray-200 px-4 py-3">Synthetic polymer blends</td>
            </tr>
            <tr>
              <td className="border border-gray-200 px-4 py-3 font-medium">Ends in &quot;-cone&quot; or &quot;-siloxane&quot;</td>
              <td className="border border-gray-200 px-4 py-3">Dimethicone, Cyclopentasiloxane</td>
              <td className="border border-gray-200 px-4 py-3">Silicone polymers</td>
            </tr>
            <tr className="bg-gray-50">
              <td className="border border-gray-200 px-4 py-3 font-medium">Contains &quot;Nylon&quot; or &quot;Polyamide&quot;</td>
              <td className="border border-gray-200 px-4 py-3">Nylon-12, Polyamide-12</td>
              <td className="border border-gray-200 px-4 py-3">Synthetic fibers</td>
            </tr>
            <tr>
              <td className="border border-gray-200 px-4 py-3 font-medium">Contains &quot;Carbomer&quot;</td>
              <td className="border border-gray-200 px-4 py-3">Carbomer 940, Carbomer 934</td>
              <td className="border border-gray-200 px-4 py-3">Polyacrylic acid polymer</td>
            </tr>
            <tr className="bg-gray-50">
              <td className="border border-gray-200 px-4 py-3 font-medium">Contains &quot;Acrylate&quot;</td>
              <td className="border border-gray-200 px-4 py-3">Ethyl Acrylate, Methyl Methacrylate</td>
              <td className="border border-gray-200 px-4 py-3">Acrylic polymers</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* MicroPlastics App Section */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Skip the Guesswork: Let the MicroPlastics App Do It for You
      </h2>
      <p>
        Reading ingredient labels is a valuable skill, but it is also time-consuming and error-prone.
        Chemical names are confusing, manufacturers frequently change formulations, and new synthetic
        polymers are introduced regularly. Even experienced label readers can miss hidden microplastic
        ingredients, especially when they appear under unfamiliar trade names or in complex multi-word
        chemical designations.
      </p>
      <p>
        The <strong>MicroPlastics app</strong> eliminates this entire problem. Simply scan a product
        barcode with your phone camera or search by product name, and the app instantly analyzes every
        ingredient against a comprehensive database of known microplastic and synthetic polymer
        compounds. Within seconds, you receive a clear safety score from A (safest) to F (highest
        concern), along with a detailed breakdown of exactly which ingredients are flagged and why.
      </p>
      <p>
        The app catches ingredients that even careful label readers might miss, including polymers
        listed under obscure trade names, newly introduced synthetic compounds, and ingredients that
        are technically microplastics but rarely discussed in mainstream consumer guides. It also tracks
        your cumulative microplastic exposure over time, helping you see the real-world impact of
        switching to cleaner products.
      </p>
      <p>
        For a step-by-step walkthrough of how to use the app, including barcode scanning, product search,
        and exposure tracking, check out our{' '}
        <Link href="/blog/check-products-for-microplastics-before-buying" className="text-[#1E6B47] hover:underline font-medium">
          complete guide to checking products for microplastics
        </Link>.
      </p>

      {/* Conclusion */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Take Control of What Goes on and in Your Body
      </h2>
      <p>
        The 10 ingredients covered in this guide represent the most common and most concerning
        microplastic compounds hiding in everyday consumer products. From the polyethylene beads in
        your face wash to the dimethicone in your hair conditioner, synthetic polymers are woven into
        nearly every product category on the market. Manufacturers add them because they are cheap,
        effective, and consumers rarely question their presence.
      </p>
      <p>
        But now you know what to look for. You know the chemical names. You know the label patterns.
        And you know that tools like the MicroPlastics app can automate the detection process entirely,
        giving you instant clarity about any product before it touches your skin or enters your home.
      </p>
      <p>
        You do not need to overhaul your entire routine overnight. Start by checking the products you
        use most frequently -- your daily moisturizer, your shampoo, your toothpaste, your foundation.
        Replace the worst offenders first and work outward from there. Every product you swap for a
        cleaner alternative is one less source of microplastic exposure for you and your family.
      </p>
      <p className="font-semibold text-[#1E6B47]">
        Download the MicroPlastics app today and scan the products in your bathroom. The ingredients
        you cannot pronounce might be the ones you should worry about most.
      </p>

      <BlogWhatTheAppChecks
        items={[
          <>Ingredient list parsed from the product label or barcode.</>,
          <>Flagged ingredients, polyethylene, acrylates, carbomer, PEG, fragrance, parabens.</>,
          <>Product category, leave-on vs rinse-off; risk weighted differently.</>,
          <>Brand and product line, clean certifications (EWG VERIFIED, MADE SAFE).</>,
          <>Cited research and regulatory references for each scan.</>,
        ]}
      />

      <BlogAppCTA
        headline="Scan personal-care products before buying"
        sub="The MicroPlastics app reads the ingredient list, flags microplastic polymers and additives, and points to cleaner alternatives in the same category."
        buttonLabel="Scan cosmetics in the app"
      />


      <BlogFAQ
        items={[
          {
            question: 'How do I identify microplastics in product ingredient lists?',
            answer:
              'Watch for any ingredient with "poly-" prefix (polyethylene, polypropylene, polyurethane, polyacrylate, polyquaternium) or "-mer" suffix. Also flag carbomer, PEG (polyethylene glycol), dimethicone, and acrylates copolymer. These are all synthetic polymers, what microplastics are made of.',
          },
          {
            question: 'Is dimethicone a microplastic?',
            answer:
              'Dimethicone is a silicone-based polymer (polydimethylsiloxane). Technically it is not a microplastic in the strict carbon-polymer sense, but it behaves similarly: persistent, accumulates in the environment, and is currently being phased out of EU cosmetics for ecological reasons. Treat it as concerning but distinct from carbon-based microplastics.',
          },
          {
            question: 'Is PEG safe in cosmetics?',
            answer:
              'Polyethylene glycol (PEG) is widely used as a solubilizer and humectant. It is a polymer derived from petrochemicals, can be contaminated with 1,4-dioxane (a possible carcinogen) during manufacture, and is being restricted in EU cosmetics. Cleaner brands replace it with plant-derived alternatives.',
          },
          {
            question: 'Which everyday product is the highest-priority swap?',
            answer:
              'Toothpaste, because you use it twice daily, ingest a small amount, and most major brands contain polyethylene, carbomer, PEG, or acrylates copolymer. Switching to a verified plastic-free toothpaste is a cheap change that does a lot.',
          },
          {
            question: 'Are "natural" or "clean beauty" labels reliable?',
            answer:
              'Not on their own, both terms are loosely regulated. Verify by ingredient list. Trustworthy third-party signals: COSMOS-certified organic, Ecocert, Zero Plastic Inside, Plastic-Free Trust Mark. Use the scanner app to parse the list automatically.',
          },
        ]}
      />

      <BlogSources
        items={[
          {
            authors: 'Research collaboration',
            year: 2025,
            title: 'Patterns and risks of microplastic release during primary oral care in Chinese residents',
            publisher: 'Ecotoxicology and Environmental Safety',
            url: 'https://www.sciencedirect.com/science/article/pii/S0147651325010395',
          },
          {
            authors: 'European Commission',
            year: 2026,
            title: 'Commission Regulation (EU) 2026/1168 amending REACH Annex XVII, Entry 78 (synthetic polymer microparticles)',
            publisher: 'European Commission',
            url: 'https://en.reach24h.com/news/industry-news/chemical/reach-microplastics-regulation-2026-1168',
          },
          {
            authors: 'European Chemicals Agency (ECHA)',
            year: 2023,
            title: 'Restriction on intentionally added microplastics',
            publisher: 'ECHA',
            url: 'https://echa.europa.eu/hot-topics/microplastics',
          },
          {
            authors: 'US FDA / Microbead-Free Waters Act',
            year: 2015,
            title: 'The Microbead-Free Waters Act FAQs',
            publisher: 'US FDA',
            url: 'https://www.fda.gov/cosmetics/cosmetics-laws-regulations/microbead-free-waters-act-faqs',
          },
          {
            authors: 'Rochester JR, Bolden AL',
            year: 2015,
            title: 'Bisphenol S and F: Systematic Review and Comparison of the Hormonal Activity of Bisphenol A Substitutes',
            publisher: 'Environmental Health Perspectives',
            url: 'https://ehp.niehs.nih.gov/doi/10.1289/ehp.1408989',
          },
          {
            authors: 'Plastic Soup Foundation / Beat the Microbead',
            year: 2024,
            title: 'Plastics in Personal Care Products: searchable database',
            publisher: 'Plastic Soup Foundation',
            url: 'https://www.beatthemicrobead.org/',
          },
          {
            authors: 'Environmental Working Group',
            year: 2024,
            title: 'EWG\'s Skin Deep Cosmetics Database',
            publisher: 'EWG',
            url: 'https://www.ewg.org/skindeep/',
          },
        ]}
      />


    </BlogPostLayout>
  )
}
